import {
  sortPeriods,
  getWeekNumber,
} from '@/components/Reports/reportPeriodHeatmapUtils';

const HEATMAP_PERIODS = 6;

export const METRICS = [
  '% in SLA',
  '# of Units out of SLA',
];

export const PERIODICITIES = [ 'Week', 'Month', 'Year' ];
export const BASE_DATES = [ 'Due Date', 'Created Date' ];
export const LTM_TYPES = [ 'Laser', 'Embroidery' ];

function safeDivide(a, b) {
  if (!b) return 0;
  return a / b;
}

function enrichCell(cell, metric) {
  const fullSla = (cell.slaUnits || 0) + (cell.slaPendingUnits || 0);
  const unitsOut =
    (cell.nonSlaUnits || 0) + (cell.nonSlaPendingUnits || 0) + (cell.jammedExpired || 0);
  const fullSlaOrders = (cell.slaOrders || 0) + (cell.slaPendingOrders || 0);
  const ordersOut =
    (cell.nonSlaOrders || 0) + (cell.nonSlaPendingOrders || 0) + (cell.jammedOrders || 0);
  const pctInSla = Math.min(safeDivide(fullSla, cell.totalUnits) * 100, 100);
  const pctInSlaOrders = Math.min(safeDivide(fullSlaOrders, cell.totalOrders) * 100, 100);

  const metricValue = metric === '# of Units out of SLA' ? unitsOut : round1(pctInSla);

  return {
    ...cell,
    fullSla,
    unitsOut,
    fullSlaOrders,
    ordersOut,
    pctInSla: round1(pctInSla),
    pctInSlaOrders: round1(pctInSlaOrders),
    metricValue,
  };
}

function round1(v) {
  return Math.round((Number(v) || 0) * 10) / 10;
}

export function filterCells(cells, { brands, locations, baseDate, slaType }) {
  const brandSet = new Set((brands || []).map(String));
  const locationSet = new Set((locations || []).map(String));
  return (cells || []).filter((c) => {
    if (baseDate) {
      const cellBase = c.baseDate || c.base_date;
      if (cellBase && cellBase !== baseDate) return false;
    }
    if (!matchesLtmType(c, slaType)) {
      return false;
    }
    const brand = c.brandName || c.brand_name;
    const location = c.location;
    if (brandSet.size && !brandSet.has(String(brand))) {
      return false;
    }
    if (locationSet.size && !locationSet.has(String(location))) {
      return false;
    }
    return true;
  });
}

export function filterMetricSlices(slices, { brands, locations, slaType }) {
  const brandSet = new Set((brands || []).map(String));
  const locationSet = new Set((locations || []).map(String));
  return (slices || []).filter((s) => {
    if (!matchesLtmType(s, slaType)) {
      return false;
    }
    const brand = s.brandName || s.brand_name;
    const location = s.location;
    if (brandSet.size && !brandSet.has(String(brand))) {
      return false;
    }
    if (locationSet.size && !locationSet.has(String(location))) {
      return false;
    }
    return true;
  });
}

function matchesLtmType(row, slaType) {
  if (!slaType) {
    return true;
  }
  if (slaType === 'Laser') {
    const v = row.isLaser ?? row.is_laser;
    return v === true || v === null || v === undefined;
  }
  if (slaType === 'Embroidery') {
    const v = row.isEmbroidery ?? row.is_embroidery;
    return v === true || v === null || v === undefined;
  }
  return true;
}

export function aggregateMetricCallouts(slices) {
  const empty = {
    ecomWeek: 0,
    ecomMonth: 0,
    ltmWeek: 0,
    ltmMonth: 0,
    ecomWeekOut: 0,
    ecomWeekTotal: 0,
    ecomMonthOut: 0,
    ecomMonthTotal: 0,
    ltmWeekOut: 0,
    ltmWeekTotal: 0,
    ltmMonthOut: 0,
    ltmMonthTotal: 0,
  };
  if (!slices?.length) return empty;

  const sum = (flag, window) => {
    const rows = slices.filter((s) => s.flag === flag && s.window === window);
    const total = rows.reduce((a, r) => a + (r.totalUnits || 0), 0);
    const sla = rows.reduce((a, r) => a + (r.slaUnits || 0), 0);
    const nonSla = rows.reduce((a, r) => a + (r.nonSlaUnits || 0), 0);
    const value = Math.min(safeDivide(sla, total) * 100, 100);
    return { value: round1(value), nonSla, total };
  };

  const ecomWeek = sum('ECOM', 'WEEK');
  const ecomMonth = sum('ECOM', 'MONTH');
  const ltmWeek = sum('LTM', 'WEEK');
  const ltmMonth = sum('LTM', 'MONTH');

  return {
    ecomWeek: ecomWeek.value,
    ecomMonth: ecomMonth.value,
    ltmWeek: ltmWeek.value,
    ltmMonth: ltmMonth.value,
    ecomWeekOut: ecomWeek.nonSla,
    ecomWeekTotal: ecomWeek.total,
    ecomMonthOut: ecomMonth.nonSla,
    ecomMonthTotal: ecomMonth.total,
    ltmWeekOut: ltmWeek.nonSla,
    ltmWeekTotal: ltmWeek.total,
    ltmMonthOut: ltmMonth.nonSla,
    ltmMonthTotal: ltmMonth.total,
  };
}

/** Collapse location grain → brand × period for heatmap (sum units). */
export function collapseToBrandPeriod(cells, metric) {
  const map = new Map();
  cells.forEach((raw) => {
    const key = `${raw.brandName}||${raw.periodKey}`;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, { ...raw });
      return;
    }
    [
      'totalUnits', 'slaUnits', 'slaPendingUnits', 'nonSlaUnits', 'nonSlaPendingUnits',
      'jammedExpired', 'totalOrders', 'slaOrders', 'slaPendingOrders', 'nonSlaOrders',
      'nonSlaPendingOrders', 'jammedOrders',
    ].forEach((f) => {
      existing[f] = (existing[f] || 0) + (raw[f] || 0);
    });
  });
  return [...map.values()].map((c) => enrichCell(c, metric));
}

export {
  sortPeriods,
  getWeekNumber,
};

export function buildHeatmapModel(cells, metric, periodicity, _tableFlag, _slaType, noteSummaries) {
  const enriched = collapseToBrandPeriod(cells, metric);
  if (!enriched.length) {
    return { rowData: [], periods: [], pinnedBottom: []};
  }

  let periods = sortPeriods(enriched.map((c) => c.periodKey), periodicity);
  if (periods.length > HEATMAP_PERIODS) {
    periods = periods.slice(-HEATMAP_PERIODS);
  }
  const periodSet = new Set(periods);
  const filtered = enriched.filter((c) => periodSet.has(c.periodKey));
  const brands = [...new Set(filtered.map((c) => c.brandName))].sort();
  const notesByCell = indexNoteSummaries(noteSummaries);

  const perc = metric !== '# of Units out of SLA';
  const rowData = brands.map((brand) => {
    const row = { BRAND_NAME: brand };
    periods.forEach((p) => {
      const cell = filtered.find((c) => c.brandName === brand && c.periodKey === p);
      row[p] = cell ? cell.metricValue : null;
      const note = notesByCell.get(`${brand}||${p}`);
      row[`${p}_tooltip`] = cell
        ? buildTooltip(cell, metric, perc, note)
        : null;
      row[`${p}_hasNotes`] = !!(note && note.count > 0);
    });
    return row;
  });

  const pinned = { BRAND_NAME: 'Total' };
  periods.forEach((p) => {
    const periodCells = filtered.filter((c) => c.periodKey === p);
    if (!periodCells.length) {
      pinned[p] = null;
      return;
    }
    if (metric === '# of Units out of SLA') {
      pinned[p] = periodCells.reduce((a, c) => a + c.unitsOut, 0);
    } else {
      const num = periodCells.reduce((a, c) => a + c.fullSla, 0);
      const den = periodCells.reduce((a, c) => a + c.totalUnits, 0);
      pinned[p] = round1(Math.min(safeDivide(num, den) * 100, 100));
    }
  });

  return { rowData, periods, pinnedBottom: [pinned]};
}

function indexNoteSummaries(summaries) {
  const map = new Map();
  (summaries || []).forEach((s) => {
    if (!s || !s.brandName || !s.periodKey) return;
    map.set(`${s.brandName}||${s.periodKey}`, s);
  });
  return map;
}

function buildTooltip(cell, metric, perc, noteSummary) {
  const fmt = (n) => Number(n || 0).toLocaleString('en-US');
  const noteCount = noteSummary && noteSummary.count > 0 ? noteSummary.count : 0;
  return {
    periodKey: cell.periodKey,
    brandName: cell.brandName,
    metric,
    metricValue: cell.metricValue,
    suffix: perc ? '%' : '',
    pctInSla: cell.pctInSla,
    fullSla: fmt(cell.fullSla),
    slaPendingUnits: fmt(cell.slaPendingUnits),
    unitsOut: fmt(cell.unitsOut),
    jammedExpired: fmt(cell.jammedExpired),
    pctInSlaOrders: cell.pctInSlaOrders,
    fullSlaOrders: fmt(cell.fullSlaOrders),
    slaPendingOrders: fmt(cell.slaPendingOrders),
    ordersOut: fmt(cell.ordersOut),
    jammedOrders: fmt(cell.jammedOrders),
    noteCount,
    noteAuthor: noteCount ? (noteSummary.latestAuthorName || 'User') : '',
    notePreview: noteCount ? truncate(noteSummary.latestBody || '', 140) : '',
  };
}

function truncate(text, max) {
  const s = String(text || '');
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

export function isMonthEnd(date) {
  const d = new Date(date);
  const next = new Date(d);
  next.setDate(d.getDate() + 1);
  return next.getDate() === 1;
}

export function isYearEnd(date) {
  const d = new Date(date);
  return d.getMonth() === 11 && d.getDate() === 31;
}

export function formatUpdatedAt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}, 12:00 AM`;
}
